import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/app/shell/AppShell";
import { useSession } from "@/lib/auth/session";
import { MixcoreLoader } from "@/ui/brand/MixcoreLoader";
import { LoginScreen } from "@/modules/auth/LoginScreen";
import { PulseScreen } from "@/modules/pulso/PulseScreen";
import { ExecutiveScreen } from "@/modules/dashboard/ExecutiveScreen";
import { OperationalScreen } from "@/modules/dashboard/OperationalScreen";
import { AnalyticalScreen } from "@/modules/dashboard/AnalyticalScreen";
import { AppAnalyticsScreen } from "@/modules/dashboard/app-analytics/AppAnalyticsScreen";
import { ProducersScreen } from "@/modules/applications/ProducersScreen";
import { ArtistsScreen } from "@/modules/applications/ArtistsScreen";
import { ProvidersScreen } from "@/modules/applications/ProvidersScreen";
import { JobsScreen } from "@/modules/applications/JobsScreen";
import { InboxScreen } from "@/modules/applications/InboxScreen";
import { SeguimientoScreen } from "@/modules/seguimiento/SeguimientoScreen";
import { MessagesScreen } from "@/modules/communications/MessagesScreen";
import { ContactScreen } from "@/modules/communications/ContactScreen";
import { CommunityScreen } from "@/modules/communications/CommunityScreen";
import { ModerationScreen } from "@/modules/governance/ModerationScreen";
import { IncidentsScreen } from "@/modules/governance/IncidentsScreen";
import { BlacklistScreen } from "@/modules/governance/BlacklistScreen";
import { AppealsScreen } from "@/modules/governance/AppealsScreen";
import { MpHealthScreen } from "@/modules/operations/MpHealthScreen";
import { MpAuditScreen } from "@/modules/operations/MpAuditScreen";
import { FeaturesScreen } from "@/modules/operations/FeaturesScreen";
import { DataExplorerScreen } from "@/modules/operations/DataExplorerScreen";
import { TaxDocumentsScreen } from "@/modules/tax/DocumentsScreen";
import { TaxConfigScreen } from "@/modules/tax/ConfigScreen";
import { ThemeScreen } from "@/modules/theme/ThemeScreen";
import { UsersScreen as ControlUsersScreen } from "@/modules/control/UsersScreen";
import { RolesScreen as ControlRolesScreen } from "@/modules/control/RolesScreen";
import { AuditScreen as ControlAuditScreen } from "@/modules/control/AuditScreen";
import { MyProfileScreen as ControlMyProfileScreen } from "@/modules/control/MyProfileScreen";
import { BoardScreen as SettlementsBoardScreen } from "@/modules/settlements/BoardScreen";
import { PendingScreen as SettlementsPendingScreen } from "@/modules/settlements/PendingScreen";
import { DisputesScreen as SettlementsDisputesScreen } from "@/modules/settlements/DisputesScreen";
import { ToTransferScreen as SettlementsToTransferScreen } from "@/modules/settlements/ToTransferScreen";
import { HistoryScreen as SettlementsHistoryScreen } from "@/modules/settlements/HistoryScreen";
import { ProducersScreen as SettlementsProducersScreen } from "@/modules/settlements/ProducersScreen";
import { ReportsScreen as SettlementsReportsScreen } from "@/modules/settlements/ReportsScreen";
import { DocsScreen } from "@/modules/docs/DocsScreen";
import { ContentScreen } from "@/modules/content/ContentScreen";
import { ReviewsScreen } from "@/modules/content/ReviewsScreen";
import { PricingScreen } from "@/modules/billing/PricingScreen";
import { EntitlementsScreen } from "@/modules/billing/EntitlementsScreen";
import { PromoCodesScreen } from "@/modules/billing/PromoCodesScreen";
import { NewsletterScreen } from "@/modules/newsletter/NewsletterScreen";
import { DemoScreen } from "@/modules/demo/DemoScreen";
import { ProducersScreen as ProducersMgmtScreen } from "@/modules/producers-mgmt/ProducersScreen";
import { ProducerDetailScreen as ProducersMgmtDetailScreen } from "@/modules/producers-mgmt/ProducerDetailScreen";
import { ArtistsScreen as ArtistsMgmtScreen } from "@/modules/artists-mgmt/ArtistsScreen";
import { ArtistDetailScreen as ArtistsMgmtDetailScreen } from "@/modules/artists-mgmt/ArtistDetailScreen";
import { RecruitersScreen } from "@/modules/recruiters/admin/RecruitersScreen";
import { RecruiterDetailScreen } from "@/modules/recruiters/admin/RecruiterDetailScreen";
import { MyWorkScreen } from "@/modules/recruiters/self/MyWorkScreen";
import { NAVIGATION } from "@/app/navigation";
import { ModulePlaceholder } from "@/ui/patterns/ModulePlaceholder";
import { ForbiddenScreen } from "@/modules/auth/ForbiddenScreen";
import { usePermission } from "@/lib/permissions/useCan";
import type { Permission } from "@/lib/permissions/catalog";
import { PERMISSIONS } from "@/lib/permissions/catalog";

/**
 * Portada según el rol: el reclutador (permiso explícito, sin Pulso) entra a
 * "Mi trabajo"; un rol acotado (ej. sólo Seguimiento) a su primera sección
 * visible; el resto, al Pulso como siempre.
 */
function HomeRedirect() {
  const user = useSession((s) => s.user);
  const has = useSession((s) => s.hasPermission);
  const explicit = user?.permissions ?? [];
  if (has(PERMISSIONS.PULSE_VIEW)) return <Navigate to="/pulse" replace />;
  if (explicit.includes(PERMISSIONS.RECRUITERS_SELF)) return <Navigate to="/reclutador" replace />;
  const first = NAVIGATION.find((g) => (g.exactPermission ? explicit.includes(g.requiredPermission as string) : has(g.requiredPermission)));
  return <Navigate to={first?.to ?? "/pulse"} replace />;
}

/** Guard de ruta por permiso. Si no tiene, muestra ForbiddenScreen. */
function Guard({ permission, children }: { permission: Permission | string; children: React.ReactNode }) {
  const allowed = usePermission(permission);
  if (!allowed) return <ForbiddenScreen permission={permission} />;
  return <>{children}</>;
}

export function AppRouter() {
  const user = useSession((s) => s.user);
  const loading = useSession((s) => s.loading);

  // El mismo disco del splash de index.html, en la misma fase de giro.
  if (loading) {
    return <MixcoreLoader label="Cargando Nexus…" />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/login" element={<HomeRedirect />} />

        <Route
          path="/pulse"
          element={
            <Guard permission={PERMISSIONS.PULSE_VIEW}>
              <PulseScreen />
            </Guard>
          }
        />

        {/* Dashboard */}
        <Route path="/dashboard" element={<Navigate to="/dashboard/executive" replace />} />
        <Route
          path="/dashboard/executive"
          element={
            <Guard permission={PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW}>
              <ExecutiveScreen />
            </Guard>
          }
        />
        <Route
          path="/dashboard/operational"
          element={
            <Guard permission={PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW}>
              <OperationalScreen />
            </Guard>
          }
        />
        <Route
          path="/dashboard/analytical"
          element={
            <Guard permission={PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW}>
              <AnalyticalScreen />
            </Guard>
          }
        />
        <Route
          path="/dashboard/app"
          element={
            <Guard permission={PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW}>
              <AppAnalyticsScreen />
            </Guard>
          }
        />

        {/* Settlements */}
        <Route path="/settlements" element={<Navigate to="/settlements/board" replace />} />
        <Route path="/settlements/board" element={<Guard permission={PERMISSIONS.SETTLEMENTS_VIEW}><SettlementsBoardScreen /></Guard>} />
        <Route
          path="/settlements/pending"
          element={
            <Guard permission={PERMISSIONS.SETTLEMENTS_VIEW}>
              <SettlementsPendingScreen
                variant="all"
                eyebrow="Liquidaciones · Pendientes"
                title="Pendientes de revisión"
                subtitle="Liquidaciones generadas esperando que el productor las acepte o dispute. Aceptación tácita a los 3 días si no responde."
              />
            </Guard>
          }
        />
        <Route
          path="/settlements/review"
          element={
            <Guard permission={PERMISSIONS.SETTLEMENTS_VIEW}>
              <SettlementsPendingScreen
                variant="active"
                eyebrow="Liquidaciones · En revisión"
                title="En revisión activa"
                subtitle="Subset de pendientes con recordatorio enviado al productor — la ventana está corriendo activamente."
              />
            </Guard>
          }
        />
        <Route path="/settlements/disputes" element={<Guard permission={PERMISSIONS.SETTLEMENTS_VIEW}><SettlementsDisputesScreen /></Guard>} />
        <Route path="/settlements/to-transfer" element={<Guard permission={PERMISSIONS.SETTLEMENTS_VIEW}><SettlementsToTransferScreen /></Guard>} />
        <Route path="/settlements/history" element={<Guard permission={PERMISSIONS.SETTLEMENTS_VIEW}><SettlementsHistoryScreen /></Guard>} />
        <Route path="/settlements/producers" element={<Guard permission={PERMISSIONS.SETTLEMENTS_VIEW}><SettlementsProducersScreen /></Guard>} />
        <Route path="/settlements/reports" element={<Guard permission={PERMISSIONS.SETTLEMENTS_REPORTS_VIEW}><SettlementsReportsScreen /></Guard>} />

        {/* Governance */}
        <Route path="/governance" element={<Navigate to="/governance/moderation" replace />} />
        <Route path="/governance/moderation" element={<Guard permission={PERMISSIONS.MODERATION_VIEW}><ModerationScreen /></Guard>} />
        <Route path="/governance/incidents" element={<Guard permission={PERMISSIONS.INCIDENTS_VIEW}><IncidentsScreen /></Guard>} />
        <Route path="/governance/blacklist" element={<Guard permission={PERMISSIONS.BLACKLIST_VIEW}><BlacklistScreen /></Guard>} />
        <Route path="/governance/appeals" element={<Guard permission={PERMISSIONS.BLACKLIST_APPEALS_RESOLVE}><AppealsScreen /></Guard>} />

        {/* Applications */}
        <Route path="/applications" element={<Navigate to="/applications/inbox" replace />} />
        <Route path="/applications/inbox" element={<Guard permission={PERMISSIONS.APPS_PRODUCERS_VIEW}><InboxScreen /></Guard>} />
        <Route path="/applications/producers" element={<Guard permission={PERMISSIONS.APPS_PRODUCERS_VIEW}><ProducersScreen /></Guard>} />
        <Route path="/applications/artists" element={<Guard permission={PERMISSIONS.APPS_ARTISTS_VIEW}><ArtistsScreen /></Guard>} />
        <Route path="/applications/providers" element={<Guard permission={PERMISSIONS.APPS_PROVIDERS_VIEW}><ProvidersScreen /></Guard>} />
        <Route path="/applications/jobs" element={<Guard permission={PERMISSIONS.APPS_JOBS_VIEW}><JobsScreen /></Guard>} />
        {/* Invitaciones se absorbió en Seguimiento (pipeline unificado). Redirect para no romper bookmarks. */}
        <Route path="/applications/invitations" element={<Navigate to="/seguimiento/invitations" replace />} />

        {/* Seguimiento — CRM de prospectos + Kanban + validación + métricas */}
        <Route path="/seguimiento" element={<Navigate to="/seguimiento/funnel" replace />} />
        <Route path="/seguimiento/:tab" element={<Guard permission={PERMISSIONS.SEGUIMIENTO_VIEW}><SeguimientoScreen /></Guard>} />

        {/* Programa de Reclutadores (F9): gestión de Mixcore + "Mi trabajo" del reclutador */}
        <Route path="/reclutadores" element={<Guard permission={PERMISSIONS.RECRUITERS_VIEW}><RecruitersScreen /></Guard>} />
        <Route path="/reclutadores/:id" element={<Guard permission={PERMISSIONS.RECRUITERS_VIEW}><RecruiterDetailScreen /></Guard>} />
        <Route path="/reclutador" element={<Guard permission={PERMISSIONS.RECRUITERS_SELF}><MyWorkScreen /></Guard>} />
        <Route path="/reclutador/:tab" element={<Guard permission={PERMISSIONS.RECRUITERS_SELF}><MyWorkScreen /></Guard>} />

        {/* Communications */}
        <Route path="/communications" element={<Navigate to="/communications/messages" replace />} />
        <Route path="/communications/messages" element={<Guard permission={PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW}><MessagesScreen /></Guard>} />
        <Route path="/communications/contact" element={<Guard permission={PERMISSIONS.COMMS_CONTACT_VIEW}><ContactScreen /></Guard>} />
        <Route path="/communications/community" element={<Guard permission={PERMISSIONS.COMMS_COMMUNITY_VIEW}><CommunityScreen /></Guard>} />

        {/* Operations */}
        <Route path="/operations" element={<Navigate to="/operations/mp-health" replace />} />
        <Route path="/operations/mp-health" element={<Guard permission={PERMISSIONS.OPS_MP_HEALTH_VIEW}><MpHealthScreen /></Guard>} />
        <Route path="/operations/mp-audit" element={<Guard permission={PERMISSIONS.OPS_MP_AUDIT_VIEW}><MpAuditScreen /></Guard>} />
        <Route path="/operations/features" element={<Guard permission={PERMISSIONS.OPS_FEATURES_VIEW}><FeaturesScreen /></Guard>} />
        <Route path="/operations/data" element={<Guard permission={PERMISSIONS.OPS_DATA_VIEW}><DataExplorerScreen /></Guard>} />
        <Route path="/operations/tax" element={<Guard permission={PERMISSIONS.TAX_VIEW}><TaxDocumentsScreen /></Guard>} />
        <Route path="/operations/tax-config" element={<Guard permission={PERMISSIONS.TAX_VIEW}><TaxConfigScreen /></Guard>} />

        {/* Producers Management — gestión de cuentas de productor */}
        <Route path="/producers" element={<Guard permission={PERMISSIONS.PRODUCERS_MGMT_VIEW}><ProducersMgmtScreen /></Guard>} />
        <Route path="/producers/:id" element={<Guard permission={PERMISSIONS.PRODUCERS_MGMT_VIEW}><ProducersMgmtDetailScreen /></Guard>} />

        <Route path="/artists" element={<Guard permission={PERMISSIONS.ARTISTS_MGMT_VIEW}><ArtistsMgmtScreen /></Guard>} />
        <Route path="/artists/:id" element={<Guard permission={PERMISSIONS.ARTISTS_MGMT_VIEW}><ArtistsMgmtDetailScreen /></Guard>} />

        {/* Comunidad — feed cultural editorial */}
        <Route path="/content" element={<Guard permission={PERMISSIONS.CONTENT_VIEW}><ContentScreen /></Guard>} />
        <Route path="/content/reviews" element={<Guard permission={PERMISSIONS.CONTENT_MANAGE}><ReviewsScreen /></Guard>} />

        {/* Facturación — precios paramétricos + entitlements */}
        <Route path="/billing" element={<Guard permission={PERMISSIONS.BILLING_SKUS_VIEW}><PricingScreen /></Guard>} />
        <Route path="/billing/entitlements" element={<Guard permission={PERMISSIONS.BILLING_ENTITLEMENTS_VIEW}><EntitlementsScreen /></Guard>} />
        <Route path="/billing/promo-codes" element={<Guard permission={PERMISSIONS.PROMO_CODES_VIEW}><PromoCodesScreen /></Guard>} />

        {/* Newsletter (#14) */}
        <Route path="/newsletter" element={<Guard permission={PERMISSIONS.NEWSLETTER_MANAGE}><NewsletterScreen /></Guard>} />

        {/* Theme */}
        <Route path="/theme" element={<Guard permission={PERMISSIONS.THEME_VIEW}><ThemeScreen /></Guard>} />

        {/* Demo — cuenta showroom única para socios comerciales */}
        <Route path="/demo" element={<Guard permission={PERMISSIONS.DEMO_VIEW}><DemoScreen /></Guard>} />

        {/* Nexus Control */}
        <Route path="/control" element={<Navigate to="/control/users" replace />} />
        <Route path="/control/users" element={<Guard permission={PERMISSIONS.NEXUS_CONTROL_MANAGE_USERS}><ControlUsersScreen /></Guard>} />
        <Route path="/control/users/me" element={<ControlMyProfileScreen />} />
        <Route path="/control/roles" element={<Guard permission={PERMISSIONS.NEXUS_CONTROL_MANAGE_ROLES}><ControlRolesScreen /></Guard>} />
        <Route path="/control/audit" element={<Guard permission={PERMISSIONS.NEXUS_CONTROL_VIEW_AUDIT}><ControlAuditScreen /></Guard>} />

        {/* Docs */}
        <Route path="/docs" element={<Guard permission={PERMISSIONS.DOCS_VIEW}><DocsScreen /></Guard>} />

        <Route path="*" element={<ModulePlaceholder title="Página no encontrada" />} />
      </Routes>
    </AppShell>
  );
}
